
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'

// constants
import {
    IndividualTariffsMain,
    IndividualTariffs,
    StandardParamsColumns,
    StandardParamsRows,
    AdditionalServicesColumns,
} from '@/core/constants/columns/IndividualTariffs.columns'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { Zone } from '@/new-services/types/location'
import locationAPI from '@/new-services/location.service'
import { FTL } from '@/core/realisations/FTL.realisation'
import { FTLCarTypeT } from '@/core/types/ftl.types'
import { billing } from '@/core/realisations/billing.realisation'
import { FtlTariffPayload } from '@/core/types/billing.types'
import { useRouter } from 'vue-router'

const TARIFF_FTL = {
    zoneId: null as number | null,
    volume: null as number | null,
    minHour: null as number | null,
    price: null as number | null,
    cost: null as number | null,
}

const STANDARD_TARIFF = [
    {
        volume: 1.5,
        zoneId: 1,
        minHour: 2,
        price: 0,
        cost: 7900,
    },
    {
        volume: 3,
        zoneId: 1,
        minHour: 3,
        price: 0,
        cost: 16000,
    },
    {
        volume: 5,
        zoneId: 1,
        minHour: 3,
        price: 0,
        cost: 16000,
    },
]

export default defineComponent({
    components: {
        TheButton,
        TheSelect,
        TheInput,
        PageTitle,
    },
    setup() {
        const notifications = useNotifications()
        const router = useRouter()

        const state = reactive({
            is_loading: false,
            //
            companyId: null as number | null,
            tariffs: [{ ...TARIFF_FTL }],
        })

        const state_form$ = useVuelidate(
            {
                companyId: { required },
                tariffs: {
                    // @ts-ignore
                    $each: helpers.forEach({
                        zoneId: { required },
                        volume: { required },
                        minHour: { required },
                        cost: { required },
                    }),
                },
            },
            state
        )

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            zones: new DataState<Zone>({}),
            ftl_car_types: new DataState<FTLCarTypeT>({}),
        })

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const loadFtlCarTypes = async () => {
            data_state.ftl_car_types.pagination.current_page = 1

            try {
                data_state.ftl_car_types.is_loading = true
                const response = await FTL.getCarTypes()

                data_state.ftl_car_types.list = response.data.filter((item) => item.value)
                data_state.ftl_car_types.pagination.total_pages = 1

                data_state.ftl_car_types.is_failed = false
            } catch (error) {
                data_state.ftl_car_types.is_failed = true
            } finally {
                data_state.ftl_car_types.is_loading = false
            }
        }

        const loadZones = async () => {
            try {
                data_state.zones.is_loading = true

                const response = await locationAPI.getZones()
                data_state.zones.list = response.data.data

                data_state.zones.is_failed = false
            } catch (error) {
                data_state.zones.is_failed = true
                console.error(error)
            } finally {
                data_state.zones.is_loading = false
            }
        }

        const addTariff = () => state.tariffs.push({ ...TARIFF_FTL })
        const deleteTariff = (index: number) => state.tariffs.splice(index, 1)
        const addStandard = () => (state.tariffs = [...STANDARD_TARIFF])

        const createFtlTariff = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload = {
                    companyId: state.companyId as number,
                    shipmentCostsFtl: [...state.tariffs],
                }
                await billing.addFtlTariff(payload as FtlTariffPayload)

                notifications.notify({ title: 'Тариф успешно создан!', type: 'success' })
                router.push('/tariffs/ftl')
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При создании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loading = computed(() => {
            return (
                state.is_loading || data_state.companies.is_loading || data_state.zones.is_loading
            )
        })

        onBeforeMount(() => {
            loadCompanies(1)
            loadZones()
            loadFtlCarTypes()
        })

        return {
            state,
            data_state,
            //
            state_form$,
            //
            createFtlTariff,
            loadZones,
            loadFtlCarTypes,
            addTariff,
            deleteTariff,
            addStandard,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            IndividualTariffsMain,
            IndividualTariffs,
            StandardParamsColumns,
            AdditionalServicesColumns,
            StandardParamsRows,
            loading,
        }
    },
})
